import React, { useState, useMemo, useEffect } from "react";
import {
  DatePicker,
  Dropdown,
  Input,
  Select,
  Table,
  Spin,
  Modal,
  notification,
} from "antd";
import { debounce } from "lodash";
import { ACTIONS_DROPDOWN_PROPS } from "@/constants/actionsDropdown";
import { useNavigate } from "react-router-dom";
import { useGetFormsQuery } from "@/store/api/apiSlice";
import {
  SearchOutlined,
  CloseOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PlayCircleOutlined,
  StopOutlined,
  InboxOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { LuFilter } from "react-icons/lu";
import { LayoutGrid, List } from "lucide-react";
import CreateFormModal from "../../../components/Modals/CreateFormModal";
import {
  useArchiveFormMutation,
  useCreateFormMutation,
  useDeleteFormMutation,
  useUnarchiveFormMutation,
  useUpdateFormStatusMutation,
} from "../../../store/api/apiSlice";
import { selectCurrentRole } from "../../../store/auth/authSlice";
import { useSelector } from "react-redux";

const FormCard = ({
  data,
  userRole,
  onRowClick,
  getActionsMenuItems,
  onActionMenuClick,
}) => (
  <div
    className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer border"
    onClick={() => onRowClick(data)}
  >
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1">
        <h3 className="font-medium text-gray-900 mb-2 mt-0">{data?.title}</h3>
        <p className="text-sm text-gray-500 mb-2 line-clamp-2">
          {data?.description}
        </p>
        <span
          className="inline-flex px-3 py-1 rounded-full text-xs border text-black"
          style={{ border: "1px solid #000" }}
        >
          {data?.type}
        </span>
      </div>
    </div>

    <div className="flex justify-between items-center mb-3">
      <div>
        <div>{data?.organization?.name}</div>
      </div>

      <div>
        <div>{data?.campaign?.name || "N/A"}</div>
      </div>
    </div>

    <div className="flex items-center justify-between">
      <span
        className={`inline-flex px-3 py-1 rounded-full text-sm capitalize ${
          data.status === "active"
            ? "bg-yellow text-yellow-800"
            : data.status === "draft"
            ? "bg-orange text-white"
            : "bg-red-500 text-white"
        }`}
      >
        {data.status === "not_active" ? "Inactive" : data.status}
      </span>
      {userRole === "admin" && (
        <Dropdown
          {...ACTIONS_DROPDOWN_PROPS}
          menu={{
            items: getActionsMenuItems(data),
            onClick: (event) => onActionMenuClick(data, event.key, event),
          }}
        >
          <button
            type="button"
            className="actions-dots-btn"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Open actions for ${data?.title || "form"}`}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      )}
    </div>
  </div>
);

const ArchivedForms = () => {
  const navigate = useNavigate();

  const userRole = useSelector(selectCurrentRole);

  const [viewType, setViewType] = useState("list");
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);

  const [filters, setFilters] = useState({
    status: undefined,
    type: undefined,
    dateRange: undefined,
  });

  const [archiveForm] = useArchiveFormMutation();
  const [deleteForm] = useDeleteFormMutation();
  const [updateFormStatus] = useUpdateFormStatusMutation();
  const [duplicateForm] = useCreateFormMutation();
  const [unarchiveForm] = useUnarchiveFormMutation();

  const {
    data: formsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetFormsQuery({
    search: searchText,
    status: filters.status,
    type: filters.type,
    start_date: filters.dateRange?.[0]?.format("YYYY-MM-DD"),
    end_date: filters.dateRange?.[1]?.format("YYYY-MM-DD"),
  });
  const forms = formsData?.data;

  const handleEdit = (record) => {
    setEditingForm(record);
    setEditModalVisible(true);
  };

  const handleUnarchive = async (record) => {
    try {
      await unarchiveForm(record._id).unwrap();
    
      notification.success({
        message: "Success",
        description: "Form unarchived successfully",
      });

      refetch();
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message ||
          error.error ||
          error.message ||
          "Failed to unarchive form",
      });
    }
  };

  const handleArchive = async (record) => {
    try {
      await archiveForm(record._id).unwrap();
   
      notification.success({
        message: "Success",
        description: "Form archived successfully",
      });

      refetch();
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message ||
          error.error ||
          error.message ||
          "Failed to archive form",
      });
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      await updateFormStatus({
        id: record._id,
        status: newStatus,
      }).unwrap();
     
      notification.success({
        message: "Success",
        description: `Form status updated to ${newStatus}`,
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.data?.message || "Failed to update status",
      });
    }
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleDuplicate = async (record) => {
    const duplicateRecord = { ...record };
    delete duplicateRecord._id;
    delete duplicateRecord.__v;

    try {
      const duplicateData = {
        ...duplicateRecord,
        organization: record.organization._id,
        is_duplicate: true,
        anonymous: false,
        campaign: null,
        start_date: Math.floor(
          new Date(record.start_date).getTime() / 1000,
        ).toString(),
        end_date: Math.floor(
          new Date(record.end_date).getTime() / 1000,
        ).toString(),
      };

      await duplicateForm(duplicateData).unwrap();
    
      notification.success({
        message: "Success",
        description: "Form duplicated successfully",
      });
    } catch (error) {}
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this form?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteForm(record._id).unwrap();
         
          notification.success({
            message: "Success",
            description: "Form deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.data?.message || "Failed to delete form",
          });
        }
      },
    });
  };

  const basePath = window.location.pathname.split("/")[1];

  const handleRowClick = (record) => {
    navigate("/" + basePath + `/forms/${record._id}`, {
      state: record,
    });
  };

  const getActionsMenuItems = (record) => {
    const items = [{ key: "edit", label: "Edit", icon: <EditOutlined /> }];

    if (record.status === "active") {
      items.push({ key: "end", label: "End", icon: <StopOutlined /> });
    } else if (record.status === "draft") {
      items.push({ key: "start", label: "Start", icon: <PlayCircleOutlined /> });
    } else {
      items.push({ key: "duplicate", label: "Duplicate", icon: <CopyOutlined /> });
    }

    if (window.location.pathname.includes("/archived")) {
      items.push({ key: "unarchive", label: "Unarchive", icon: <RollbackOutlined /> });
    } else {
      items.push({ key: "archive", label: "Add to Archive", icon: <InboxOutlined /> });
    }

    items.push({
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    });

    return items;
  };

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    if (actionKey === "edit") return handleEdit(record);
    if (actionKey === "delete") return handleDelete(record);
    if (actionKey === "archive") return handleArchive(record);
    if (actionKey === "unarchive") return handleUnarchive(record);
    if (actionKey === "duplicate") return handleDuplicate(record);
    if (actionKey === "start") return handleStatusChange(record, "active");
    if (actionKey === "end") return handleStatusChange(record, "not_active");
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchText(value);
      }, 500),
    [],
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearchText("");
    debouncedSearch("");
  };

  const columns = [
    {
      title: "Sr#",
      dataIndex: "srNo",
      key: "srNo",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Form",
      key: "form",
      render: (_, record) => (
        <div className="flex flex-col" key={record._id}>
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-md">
            {record.description}
          </div>
          <span className="inline-flex px-3 py-2 rounded-full text-xs border border-black text-gray-800 w-fit mt-1">
            {record.type}
          </span>
        </div>
      ),
    },
    {
      title: "Organisation",
      dataIndex: "organization",
      key: "organization",
      render: (_, record) => <span>{record?.organization?.name}</span>,
    },
    {
      title: "Questions",
      dataIndex: "total_questions",
      key: "total_questions",
      render: (_, record) => <span>{record?.questions?.length}</span>,
    },
    {
      title: "Responses",
      key: "responses",
      render: (_, record) => {
        const totalQuestions = record.questions.length;

        const validResponses = record.responses.filter((response) => {
          try {
            if (typeof response.response !== "string") {
              return false;
            }

            const parsed = JSON.parse(response.response);

            if (!parsed.responses || typeof parsed.responses !== "object") {
              return false;
            }

            if (Object.keys(parsed.responses).length === 0) {
              return false;
            }

            return true;
          } catch (err) {
            return false;
          }
        });

        const totalResponses = validResponses.length;
        const uniqueRespondents = new Set(validResponses.map((r) => r.user))
          .size;
        const responsePercentage =
          totalQuestions && uniqueRespondents
            ? (
                (totalResponses / (totalQuestions * uniqueRespondents)) *
                100
              ).toFixed(1)
            : "0.0";

        return (
          <span>
            {totalResponses} ({responsePercentage}
            %)
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const displayText =
          record.status === "not_active" ? "Inactive" : record.status;

        let styling = "";

        if (record.status === "active") {
          styling = "bg-yellow text-yellow-800";
        } else if (record.status === "draft") {
          styling = "bg-orange text-white";
        } else if (record.status === "not_active") {
          styling = "bg-red text-white";
        }

        return (
          <span
            className={`inline-flex px-3 py-2 rounded-full capitalize text-sm ${styling}`}
          >
            {displayText}
          </span>
        );
      },
    },
    ...(userRole === "admin"
      ? [
          {
            title: "",
            key: "actions",
            render: (_, record) => (
              <Dropdown
                {...ACTIONS_DROPDOWN_PROPS}
                menu={{
                  items: getActionsMenuItems(record),
                  onClick: (event) =>
                    handleActionMenuClick(record, event.key, event),
                }}
              >
                <button
                  type="button"
                  className="actions-dots-btn"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Open actions for ${record?.title || "form"}`}
                >
                  <MoreOutlined />
                </button>
              </Dropdown>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    refetch();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-0">
      <div className="flex items-center gap-2 flex-wrap mt-0">
        <div className="flex bg-secondary rounded-xl p-1.5">
          <button
            type="button"
            className={`view-toggle-btn ${
              viewType === "list" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("list")}
          >
            <List size={20} />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${
              viewType === "grid" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("grid")}
          >
            <LayoutGrid size={20} />
          </button>
        </div>
        <div className="relative flex-1">
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            suffix={
              searchText && (
                <CloseOutlined
                  className="cursor-pointer hover:text-gray-600"
                  onClick={clearSearch}
                />
              )
            }
            onChange={handleSearchChange}
            className="h-[48px] !rounded-2xl bg-white"
          />
        </div>
        <Select
          allowClear
          value={filters.status}
          onChange={(value) => handleFilterChange("status", value)}
          className="w-40 h-[48px] !rounded-2xl"
          placeholder="Status"
          options={[
            { value: "active", label: "Active" },
            { value: "draft", label: "Draft" },
            {
              value: "not_active",
              label: "Inactive",
            },
          ]}
        />

        <Select
          placeholder="Type"
          value={filters.type}
          onChange={(value) => handleFilterChange("type", value)}
          className="w-40 h-[48px] !rounded-2xl"
          allowClear
          options={[
            { value: "survey", label: "Survey" },
            { value: "quiz", label: "Quiz" },
          ]}
        />

        <DatePicker.RangePicker
          className="w-full md:w-[340px] h-[48px] !rounded-2xl"
          onChange={(dates) => handleFilterChange("dateRange", dates)}
          placeholder={["Start date", "End date"]}
          popupClassName="date-range-popup-mobile"
          getPopupContainer={() => document.body}
          dropdownAlign={{
            points: ["tl", "bl"],
            offset: [0, 4],
            overflow: {
              adjustX: 1,
              adjustY: 1,
            },
          }}
        />

        <button
          className={`h-[48px] w-11 flex items-center justify-center rounded-xl ${
            filterVisible ? "bg-primary text-white" : "bg-secondary"
          } border`}
          onClick={() => setFilterVisible(!filterVisible)}
        >
          <LuFilter
            size={20}
            className={filterVisible ? "text-white" : "text-red-500"}
          />
        </button>
      </div>
      {isFetching ? (
        <div className="flex justify-center p-8">
          <Spin />
        </div>
      ) : viewType === "list" ? (
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={forms}
          pagination={false}
          className="forms-table"
          rowClassName="hover:bg-gray-50 cursor-pointer"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          scroll={{ x: "100%" }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {forms.map((item) => (
            <FormCard
              key={item._id}
              data={item}
              userRole={userRole}
              onRowClick={handleRowClick}
              getActionsMenuItems={getActionsMenuItems}
              onActionMenuClick={handleActionMenuClick}
            />
          ))}
        </div>
      )}
      {editModalVisible && (
        <CreateFormModal
          visible
          onClose={() => {
            setEditModalVisible(false);
            setEditingForm(null);
          }}
          editData={editingForm}
        />
      )}
    </div>
  );
};

export default ArchivedForms;
